/**
 * SSEConnectionStatus — Status bar SSE indicator.
 *
 * Story 1.3 AC#3. Wires into StatusBar from Story 1.2.
 *
 * States:
 *   connected  → green dot + "SSE Connected"
 *   slow       → amber dot + "Connection slow — updates may be delayed"
 *   disconnected → amber dot + "Reconnecting…"
 *   polling    → amber dot + "Polling every Xs"
 *   stopped    → amber dot + "Live updates stopped" + Retry (the stream gave up after repeated failures)
 */

import { Wifi, WifiOff } from "lucide-react";
import type { SSEConnectionState } from "../../hooks/useSSEStream";

export interface SSEConnectionStatusProps {
  state: SSEConnectionState;
  /** Current polling interval in seconds (when in polling mode) */
  pollingIntervalSec?: number;
  onReconnect?: () => void;
}

const DOT_COLORS: Record<SSEConnectionState, string> = {
  connected: "bg-[var(--readiness-excellent)]",
  slow: "bg-amber-500",
  disconnected: "bg-amber-500",
  polling: "bg-amber-500",
  stopped: "bg-amber-500",
};

const STATUS_TEXT: Record<SSEConnectionState, string> = {
  connected: "SSE Connected",
  slow: "Connection slow — updates may be delayed",
  disconnected: "Reconnecting…",
  polling: "Polling",
  stopped: "Live updates stopped",
};

export function SSEConnectionStatus({
  state,
  pollingIntervalSec,
  onReconnect,
}: SSEConnectionStatusProps) {
  const Icon = state === "connected" ? Wifi : WifiOff;
  const dotColor = DOT_COLORS[state];
  let text = STATUS_TEXT[state];

  if (state === "polling" && pollingIntervalSec) {
    text = `Polling every ${pollingIntervalSec}s`;
  }

  return (
    <div className="flex items-center gap-2 text-[var(--muted-foreground)]">
      <span className={`w-2 h-2 rounded-full ${dotColor}`} aria-hidden="true" />
      <Icon className="w-3 h-3" />
      <span className="text-caption">{text}</span>
      {(state === "disconnected" ||
        state === "polling" ||
        state === "stopped") &&
        onReconnect && (
          <button
            onClick={onReconnect}
            className="text-caption text-[var(--primary)] hover:underline ml-1"
          >
            Retry now
          </button>
        )}
    </div>
  );
}
